import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BucketColorLegend } from '../components/BucketColorLegend';
import { BucketFilterChips } from '../components/BucketFilterChips';
import { DeclaredInputBanner } from '../components/DeclaredInputBanner';
import { JobCard } from '../components/JobCard';
import { JobSearchBar } from '../components/JobSearchBar';
import { JOB_SORT_OPTIONS, filterJobsByBucket, groupJobsByBucket, splitExploreShelf } from '../lib/jobBuckets';
import { useJobsInbox } from '../lib/useJobsInbox';
import styles from './Jobs.module.css';

/** D22 (Jason, 2026-07-08) — the Jobs board renders hundreds of cards with no
 *  pagination, an endless scroll on a phone. GET /jobs has no limit/offset or
 *  cursor params (confirmed against apps/api/routes/jobs.routes.ts — the
 *  route returns the full inbox in one shot), so this is honest CLIENT-SIDE
 *  windowed paging over the list the server already sent, not a server round
 *  trip. Page size mirrors the ~25-per-page convention a server-paginated
 *  route would use. */
const JOBS_PAGE_SIZE = 25;

/**
 * Jobs (Discover/Inbox) tab — LIVE against the real backend (brief (a)/(b)/(c)):
 *   (a) THE INBOX: GET /jobs (useJobsInbox), rendered through JobCard, grouped/
 *       filtered by the W1-4 bucket (BucketFilterChips) with a sort control.
 *   (b) SEARCH: POST /jobs/search (JobSearchBar) — same JobCard renderer for
 *       results — plus a "check for new jobs" button hitting POST
 *       /watchlist/poll with the server's own honest PollSummary, never an
 *       invented count.
 *   (c) CAPTURE HAND-OFF: JobCard's "Open source" link is the only capture
 *       affordance this tab offers (target _blank) — the extension (G-1) does
 *       actual capture; the empty state below says exactly that, truthfully.
 *
 * QA #34 (new-for-review + tracked sink-to-bottom) / #17 (human-readable
 * bucket labels + a color legend) / #19 ("poll" → plain copy): the ORDER
 * jobs render in and their new/tracking badges are SERVED by GET /jobs
 * (jobs.routes.ts's TRACKING_RANK + new_since_last_visit) — this page never
 * re-sorts or re-derives either signal client-side, it only renders what the
 * server already decided (no-frontend-bandaids law).
 */
export function Jobs() {
  const inbox = useJobsInbox();

  const isSearching = inbox.search.active;
  const baseList = isSearching ? inbox.search.results : inbox.state.jobs;
  const loading = isSearching ? inbox.search.loading : inbox.state.loading;
  const error = isSearching ? inbox.search.error : inbox.state.error;

  const bucketGroups = groupJobsByBucket(baseList);
  const bucketCounts: Partial<Record<string, number>> = {};
  for (const [key, jobs] of bucketGroups) bucketCounts[key] = jobs.length;
  const bucketFilteredJobs = filterJobsByBucket(baseList, inbox.bucketFilter);
  // CLASS 10 (D21), thread 3 — "a different bucket of ones to EXPLORE, not
  // fully scored." A SEPARATE dimension from the bucket filter above (a job
  // can be bucket='home' AND still provisional) — split AFTER the bucket
  // filter so the explore shelf always respects whatever bucket the user has
  // selected, never a hidden second list outside their current view.
  const { mainFlow: filteredJobs, explore: exploreJobs } = splitExploreShelf(bucketFilteredJobs);
  const [exploreOpen, setExploreOpen] = useState(false);

  // Paging composes with filters/sort/search: any change to what's IN the
  // list (a new bucket filter, a new sort, entering/leaving search, or the
  // list refreshing under a filter that no longer has enough pages) resets
  // to page 1 rather than stranding the user on a now-empty or mismatched
  // page.
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(filteredJobs.length / JOBS_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  useEffect(() => {
    setPage(0);
  }, [inbox.bucketFilter, inbox.sort, isSearching, inbox.search.query]);
  const pageStart = safePage * JOBS_PAGE_SIZE;
  const visibleJobs = filteredJobs.slice(pageStart, pageStart + JOBS_PAGE_SIZE);

  return (
    <div className={styles.page}>
      <div className={styles.eyebrow}>
        <div className={styles.kicker}>Jobs</div>
        <div className={styles.title}>Every job, in one place</div>
      </div>

      {/* D19/D17 — THE DECLARED-INPUTS ASK. Renders BEFORE search/results per
          his exact spec ("Do this, please, before you run your search") —
          the server's own served declaredInputs.missingInputs is the ONLY
          source of whether this shows; the component itself returns null
          when nothing is missing. */}
      <DeclaredInputBanner
        missingInputs={inbox.state.declaredInputs.missingInputs}
        declareState={inbox.declareState}
        onDeclare={inbox.declareAnswer}
      />

      <JobSearchBar
        query={inbox.search.query}
        loading={inbox.search.loading}
        onSearch={inbox.runSearch}
        onClear={inbox.clearSearch}
        poll={inbox.poll}
        onPollNow={inbox.runPollNow}
      />

      {isSearching && inbox.search.sweep ? (
        <div className={styles.sweepNote}>
          Checked your sources first — {inbox.search.sweep.ingestedNew} new job
          {inbox.search.sweep.ingestedNew === 1 ? '' : 's'} found before searching.
        </div>
      ) : null}

      {!isSearching ? <BucketColorLegend /> : null}

      {/* BOARD-TRUTH FIX (owner report: "I think my dream job is gone") — a
          failed bucket-PATCH (a lapsed session, a 404, a network blip) used
          to be completely silent: the card's select optimistically flipped,
          then quietly reverted to the server's true bucket on the next
          refresh with zero explanation. inbox.bucketError is jobId-scoped
          (see useJobsInbox.ts's BucketErrorState) so this names the SPECIFIC
          job whose pick didn't stick, never a vague global banner. Cleared by
          a subsequent successful pick on the same job, or dismissible here so
          it never lingers once read. */}
      {inbox.bucketError ? (
        <div className={styles.tailorErrorRow} role="alert">
          Couldn't save that bucket for "
          {baseList.find((j) => j.id === inbox.bucketError?.jobId)?.title ?? 'that job'}" —{' '}
          {inbox.bucketError.message}
          <button
            type="button"
            onClick={inbox.clearBucketError}
            className={styles.dismissBucketErrorBtn}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <div className={styles.controlsRow}>
        <BucketFilterChips
          active={inbox.bucketFilter}
          onChange={inbox.setBucketFilter}
          counts={bucketCounts}
          totalCount={baseList.length}
          sortCriteria={!isSearching ? inbox.state.declaredInputs.sortCriteria : undefined}
        />
        {!isSearching ? (
          <select
            className={styles.sortSelect}
            value={inbox.sort}
            onChange={(e) => inbox.setSort(e.target.value as typeof inbox.sort)}
            aria-label="Sort jobs"
          >
            {JOB_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {loading ? (
        <div className={styles.status}>Loading your jobs…</div>
      ) : error ? (
        <div className={styles.status} role="alert">
          Couldn't load jobs: {error}
        </div>
      ) : filteredJobs.length === 0 && exploreJobs.length === 0 ? (
        <EmptyJobsState isSearching={isSearching} hasAnyJobs={baseList.length > 0} />
      ) : (
        <>
          {filteredJobs.length > 0 ? (
            <>
              <div className={styles.list}>
                {visibleJobs.map((job) => (
                  <Link key={job.id} to={`/jobs/${job.id}`} className={styles.jobRow}>
                    <JobCard job={job} onBucketChange={inbox.setJobBucket} />
                  </Link>
                ))}
              </div>
              {pageCount > 1 ? (
                <JobsPager
                  page={safePage}
                  pageCount={pageCount}
                  pageStart={pageStart}
                  pageEnd={pageStart + visibleJobs.length}
                  total={filteredJobs.length}
                  onChange={setPage}
                />
              ) : null}
            </>
          ) : null}

          {/* CLASS 10 (D21), thread 3 — "a different bucket of ones to
              EXPLORE, not fully scored." Collapsed by default (his own
              framing: these should be OUT OF THE WAY of the main flow, not
              flooding it) — a plain disclosure, not a bucket chip, since this
              is a confidence dimension, not a bucket. */}
          {!isSearching && exploreJobs.length > 0 ? (
            <div className={styles.exploreShelf}>
              <button
                type="button"
                className={styles.exploreShelfToggle}
                onClick={() => setExploreOpen((v) => !v)}
                aria-expanded={exploreOpen}
              >
                Explore — not fully scored ({exploreJobs.length})
                <span className={styles.exploreShelfChevron} data-open={exploreOpen}>
                  ▾
                </span>
              </button>
              {exploreOpen ? (
                <div className={styles.list}>
                  {exploreJobs.map((job) => (
                    <Link key={job.id} to={`/jobs/${job.id}`} className={styles.jobRow}>
                      <JobCard job={job} onBucketChange={inbox.setJobBucket} />
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * Empty state (brief (c)): says only what is actually true — no jobs stored
 * yet, or a search/filter with zero matches. The capture hand-off line is
 * truthful about WHO does capture (the extension, on click) — this tab never
 * claims it can add a job itself.
 */
function EmptyJobsState({ isSearching, hasAnyJobs }: { isSearching: boolean; hasAnyJobs: boolean }) {
  if (isSearching) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyTitle}>No matches — yet</div>
        <div className={styles.emptyBody}>
          Nothing in your stored jobs matches that search. Try "Check for new jobs" above to look
          for anything new first.
        </div>
      </div>
    );
  }
  if (hasAnyJobs) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyTitle}>Nothing in this bucket yet</div>
        <div className={styles.emptyBody}>
          Try "All" above, or check your sources for new postings.
        </div>
      </div>
    );
  }
  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyTitle}>No jobs yet</div>
      <div className={styles.emptyBody}>
        Jobs appear here once they're saved — this tab doesn't search on its own. Install the
        browser extension to save a job as you read it, or check your sources above to pull in
        new postings.
      </div>
    </div>
  );
}

/**
 * D22 — the honest paging footer: "Showing X–Y of N" plus Prev/Next and a
 * compact numbered pager (never more than a handful of page buttons, so it
 * never wraps into its own scroll on a phone). Mobile-first — large tap
 * targets, wraps cleanly at narrow widths.
 */
function JobsPager({
  page,
  pageCount,
  pageStart,
  pageEnd,
  total,
  onChange,
}: {
  page: number;
  pageCount: number;
  pageStart: number;
  pageEnd: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const windowSize = 5;
  const windowStart = Math.max(0, Math.min(page - Math.floor(windowSize / 2), pageCount - windowSize));
  const pageNumbers = Array.from(
    { length: Math.min(windowSize, pageCount) },
    (_, i) => windowStart + i,
  );

  return (
    <div className={styles.pager}>
      <div className={styles.pagerCount}>
        Showing {pageStart + 1}–{pageEnd} of {total}
      </div>
      <div className={styles.pagerControls}>
        <button
          type="button"
          className={styles.pagerBtn}
          onClick={() => onChange(page - 1)}
          disabled={page === 0}
        >
          Prev
        </button>
        {pageNumbers.map((n) => (
          <button
            key={n}
            type="button"
            className={n === page ? styles.pagerNumActive : styles.pagerNum}
            onClick={() => onChange(n)}
            aria-current={n === page ? 'page' : undefined}
          >
            {n + 1}
          </button>
        ))}
        <button
          type="button"
          className={styles.pagerBtn}
          onClick={() => onChange(page + 1)}
          disabled={page >= pageCount - 1}
        >
          Next
        </button>
      </div>
    </div>
  );
}
